import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Container, Section, Overline, H2, Lead, FadeUp } from './shared';
import { PLAN_META, FALLBACK_PLANS, FAQS } from './landingData';

const inr = (paise) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;

export const Pricing = ({ onStart, onContact }) => {
  const [yearly, setYearly] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/billing/plans`).then((r) => r.data?.plans?.length && setPlans(r.data.plans)).catch(() => {});
  }, []);
  return (
    <Section id="pricing" testId="pricing-section" className="bg-[#0B0C0E]">
      <Container>
        <FadeUp className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="max-w-2xl">
            <Overline>Pricing</Overline>
            <H2>Simple, transparent pricing in rupees.</H2>
            <Lead className="mt-4">Start free. Upgrade when the team grows. GST invoices, UPI and cards via Razorpay.</Lead>
          </div>
          <div className="flex items-center gap-3">
            <div className="ld-toggle" data-testid="pricing-billing-toggle">
              <button data-active={!yearly} onClick={() => setYearly(false)} data-testid="billing-monthly">Monthly</button>
              <button data-active={yearly} onClick={() => setYearly(true)} data-testid="billing-annual">Yearly</button>
            </div>
            <span className="ld-badge ld-badge-coral">Save ~17%</span>
          </div>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-4 mt-14 items-stretch">
          {plans.map((p, i) => {
            const meta = PLAN_META[p.plan_id] || {};
            const popular = p.plan_id === 'pro';
            const price = p.prices[yearly ? 'yearly' : 'monthly'];
            return (
              <FadeUp key={p.plan_id} delay={i * 0.1}>
                <div className={`ld-card p-7 sm:p-8 h-full flex flex-col relative ${popular ? 'ld-card-glow' : ''}`} data-testid={`pricing-card-${p.plan_id}`}>
                  {popular && <span className="absolute -top-3 left-7 ld-badge ld-badge-coral bg-[#121316]">{meta.badge}</span>}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-100">{p.name}</h3>
                    {!popular && <span className="ld-badge">{meta.badge}</span>}
                  </div>
                  <p className="text-sm text-slate-400 mt-2 min-h-[40px]">{meta.description}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="ld-display text-4xl font-bold text-slate-50" data-testid={`pricing-price-${p.plan_id}`}>{price === 0 ? '₹0' : inr(price)}</span>
                    <span className="text-sm text-slate-500">{price === 0 ? 'forever' : yearly ? '/ year' : '/ month'}</span>
                  </div>
                  {yearly && price > 0 && <p className="text-xs text-emerald-400 mt-1">Save {inr(p.prices.monthly * 12 - p.prices.yearly)} vs monthly</p>}
                  <ul className="mt-7 space-y-3 flex-1">
                    {p.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />{f}</li>)}
                  </ul>
                  <button
                    onClick={p.plan_id === 'enterprise' ? onContact : onStart}
                    className={`mt-8 w-full ${popular ? 'ld-btn-primary' : 'ld-btn-ghost'}`}
                    data-testid={`pricing-card-${p.plan_id}-cta`}
                  >
                    {meta.cta} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </FadeUp>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-500 mt-8">All plans include 2FA, audit logs and India data residency. Usage beyond plan limits is billed transparently as add-ons.</p>
      </Container>
    </Section>
  );
};

export const FAQ = () => (
  <Section id="faq" testId="faq-section">
    <Container className="grid lg:grid-cols-12 gap-12">
      <FadeUp className="lg:col-span-4">
        <Overline>FAQ</Overline>
        <H2>Questions, answered.</H2>
        <Lead className="mt-4">Still unsure? <a href="mailto:hello@woxora.in" className="text-[#E06D53] underline underline-offset-4" data-testid="faq-contact-link">Talk to a human.</a></Lead>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-8">
        <Accordion type="single" collapsible className="ld-accordion space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="ld-acc-item !border-b" data-testid={`faq-accordion-item-${i}`}>
              <AccordionTrigger className="text-left text-slate-100 hover:no-underline py-5 text-base font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-400 leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeUp>
    </Container>
  </Section>
);
